import { getPublicStripeCurrencyCode } from "../config/public-runtime-config";

const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
// Trailing separator covers locales that render a closing period, e.g. hu-HU "2026. 08. 05."
const NUMERIC_DATE_PATTERN = /^(\d{1,4})\s*[/.\-\s]\s*(\d{1,4})\s*[/.\-\s]\s*(\d{1,4})\s*\.?$/;

type DateField = "day" | "month" | "year";

let localeDateFieldOrderCache: DateField[] | undefined;

/** The order `formatDate` puts day, month and year in for the current locale. */
function localeDateFieldOrder(): DateField[] {
  if (!localeDateFieldOrderCache) {
    localeDateFieldOrderCache = new Intl.DateTimeFormat(undefined, {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    })
      .formatToParts(new Date(2000, 0, 2))
      .map(part => part.type)
      .filter((type): type is DateField => type === "day" || type === "month" || type === "year");
  }
  return localeDateFieldOrderCache;
}

function buildLocalDate(year: number, month: number, day: number): Date | undefined {
  const date = new Date(year, month - 1, day);
  // Out-of-range parts roll over silently (month 13 becomes January), so reject them
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return undefined;
  }
  return date;
}

/**
 * Parses a date a user typed, reading numeric dates in the same field order `formatDate`
 * displays them in. `new Date("05/08/2026")` is always MM/DD/YYYY in V8, so handing it text
 * copied from a DD/MM/YYYY locale yields a different calendar day with no error.
 */
export function parseLocaleDate(dateString: string): Date | undefined {
  const trimmed = dateString.trim();
  if (!trimmed || DATE_ONLY_PATTERN.test(trimmed)) {
    return parseDate(trimmed);
  }

  const numeric = NUMERIC_DATE_PATTERN.exec(trimmed);
  const order = localeDateFieldOrder();
  if (!numeric || order.length !== 3) {
    return parseDate(trimmed);
  }

  const parts: Record<DateField, number> = { day: 0, month: 0, year: 0 };
  order.forEach((field, index) => {
    parts[field] = Number(numeric[index + 1]);
  });

  // A short year could mean either century, and `new Date` reads it as 19xx. `formatDate`
  // always writes four digits, so reject rather than guess.
  if (parts.year < 1000) {
    return undefined;
  }

  return buildLocalDate(parts.year, parts.month, parts.day);
}

/** An example date in the current locale's format, for use as input placeholder text. */
export function localeDateExample() {
  return formatDate(new Date(2025, 5, 1));
}

export function parseDate(dateString: string): Date | undefined {
  if (!dateString) {
    return undefined;
  }
  const dateOnly = DATE_ONLY_PATTERN.exec(dateString.trim());
  if (dateOnly) {
    // "YYYY-MM-DD" parses as UTC midnight, which is the previous day west of UTC,
    // so build the local calendar day instead.
    const [, year, month, day] = dateOnly;
    return buildLocalDate(Number(year), Number(month), Number(day));
  }
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    return undefined;
  }
  return date;
}

export function formatDate(
  date?: Date,
  options: Intl.DateTimeFormatOptions = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }
) {
  if (!date || date.getTime() === 0 || date.toString() === "Invalid Date") {
    return "—";
  }
  return new Intl.DateTimeFormat(undefined, options).format(date);
}

export function formatAsPrice(
  value?: number,
  options: Intl.NumberFormatOptions = {}
) {
  if (value == null) {
    return "—";
  }
  const opts: Intl.NumberFormatOptions = {
    style: "currency",
    currency: getPublicStripeCurrencyCode(),
    currencyDisplay: "narrowSymbol",
    ...options,
  };
  return new Intl.NumberFormat(undefined, opts).format(value);
}

export function formatCentsAsPrice(
  value?: number,
  options: Intl.NumberFormatOptions = {}
) {
  if (!value) {
    return "—";
  }
  const opts: Intl.NumberFormatOptions = {
    style: "currency",
    currency: getPublicStripeCurrencyCode(),
    currencyDisplay: "narrowSymbol",
    ...options,
  };
  return new Intl.NumberFormat(undefined, opts).format(value / 100);
}
export function formatNumber(value?: number, options?: Intl.NumberFormatOptions) {
  if (value == null) {
    return "—";
  }
  return new Intl.NumberFormat(undefined, options).format(value);
}
